/**
 * ComparisonWidget — voice mode.
 *
 * Two render modes, dispatched on whether `data.attributes` is provided:
 *
 *  - **Card grid** (default — `search_products` / `get_product_details`):
 *    2–3 product cards side by side on desktop, single-column on mobile.
 *    Brand + retailer + price + rating + bullets + "View product" CTA.
 *    No winner highlight — `recommendedIndex` was removed from the data
 *    shape, and the "Benny picks" badge is gone with it.
 *
 *  - **Comparison table** (`compare_products`):
 *    Columns = products (image + title + View product), rows = attributes.
 *    Mirrors the text-mode `CompareProductsWidget` exactly: same cell
 *    renderers for `price` / `rating` / `availability` / `reward` /
 *    `bullets`, plus a generic `text` and `inferred` cell that just
 *    reads `product[attr.key]`.
 *
 * Theming: every color uses `var(--voice-widget-*)` tokens populated by
 * `VoiceThemeProvider`. Brand-identity overrides (set via
 * `useThemeColors()`) flow through the same tokens.
 */
import React from 'react';
import type { ComparisonAttribute, ComparisonProduct, ComparisonWidgetData } from '../../types/widgets';
import { ConciergeRetailerIconGroup } from '../../../components/ConciergeRetailerIconGroup';
import { useIsCompactVoiceLayout } from '../../hooks/useIsCompactVoiceLayout';
import { AmazonStarRating } from './AmazonStarRating';

const WIDGET_KEYFRAMES = `
@keyframes voice-widget-fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

function formatPrice(price: ComparisonProduct['price']): string {
  if (typeof price === 'number') return `$${price.toFixed(2)}`;
  if (typeof price === 'string') return price;
  return '';
}

// ─── Card mode (search / details) ─────────────────────────────────────────────

function ProductCard({ product }: { product: ComparisonProduct }) {
  const href = product.retailerProductPageUrl;
  const showRetailerIcons =
    typeof product.retailerId === 'number' && product.retailerId > 0 && product.retailerId < 100000;

  const linkProps = href ? ({ href, target: '_blank' as const, rel: 'noreferrer' as const }) : null;
  const priceStr = formatPrice(product.price);

  return (
    <div
      style={{
        position: 'relative',
        background: 'var(--voice-widget-card-bg, #FFFFFF)',
        border: '1px solid var(--voice-widget-border, #E5E7EB)',
        borderRadius: 16,
        padding: '16px 12px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        transition: 'box-shadow 160ms ease-out, transform 160ms ease-out',
        animation: 'voice-widget-fade-in 0.28s ease-out both',
        height: '100%',
        // Per-card max-width cap (2026-05-19): keeps each product card
        // narrow + scannable even when there's only 1-2 cards on screen.
        // Grid `justifyContent: center` (see dispatcher) centers the row
        // so a single card isn't full-bleed.
        maxWidth: 230,
        width: '100%',
        boxSizing: 'border-box',
        minWidth: 0,
      }}
    >
      {product.imageUrl &&
        (linkProps ? (
          <a {...linkProps} style={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={product.imageUrl}
              alt={product.title}
              style={{
                width: 100,
                height: 100,
                objectFit: 'contain',
                borderRadius: 8,
                background: 'var(--voice-widget-img-bg, #F9FAFB)',
              }}
            />
          </a>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={product.imageUrl}
              alt={product.title}
              style={{
                width: 100,
                height: 100,
                objectFit: 'contain',
                borderRadius: 8,
                background: 'var(--voice-widget-img-bg, #F9FAFB)',
              }}
            />
          </div>
        ))}

      {product.brand && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--voice-widget-muted, #6B7280)',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            lineHeight: 1.2,
          }}
        >
          {product.brand}
        </div>
      )}

      {linkProps ? (
        <a
          {...linkProps}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--voice-widget-fg, #111827)',
            lineHeight: 1.35,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: 'calc(13px * 1.35 * 2)',
            textDecoration: 'none',
          }}
        >
          {product.title}
        </a>
      ) : (
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--voice-widget-fg, #111827)',
            lineHeight: 1.35,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: 'calc(13px * 1.35 * 2)',
          }}
        >
          {product.title}
        </div>
      )}

      {product.badge && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: 'var(--voice-widget-badge-fg, #059669)',
            background: 'var(--voice-widget-badge-bg, #ECFDF5)',
            padding: '2px 8px',
            borderRadius: 999,
            alignSelf: 'flex-start',
          }}
        >
          {product.badge}
        </div>
      )}

      <AmazonStarRating
        amazonRating={product.amazonRating}
        amazonReviewCount={product.amazonReviewCount}
        size={13}
        countFontSize={11}
      />

      {(showRetailerIcons || product.retailerName) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 18 }}>
          {showRetailerIcons && (
            <ConciergeRetailerIconGroup retailerIds={[product.retailerId as number]} iconSize={16} maxSize={3} />
          )}
          {!showRetailerIcons && product.retailerName && (
            <span style={{ fontSize: 11, color: 'var(--voice-widget-muted, #6B7280)' }}>{product.retailerName}</span>
          )}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 6,
          marginTop: 'auto',
        }}
      >
        <div
          style={{
            fontSize: 17,
            fontWeight: 700,
            color: 'var(--voice-widget-price, #111827)',
            lineHeight: 1.1,
          }}
        >
          {priceStr}
        </div>
        {product.hasRewards && product.totalRewardAmount != null && product.totalRewardAmount > 0 && (
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: 'var(--voice-widget-badge-fg, #059669)',
              background: 'var(--voice-widget-badge-bg, #ECFDF5)',
              padding: '2px 7px',
              borderRadius: 999,
              whiteSpace: 'nowrap',
            }}
          >
            ${product.totalRewardAmount.toFixed(2)} back
          </div>
        )}
      </div>

      {product.isAvailable === false && (
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--voice-widget-warn, #DC2626)' }}>
          Out of stock
        </div>
      )}

      {linkProps && (
        <a
          {...linkProps}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: 32,
            background: 'var(--voice-widget-button-bg, #111827)',
            color: 'var(--voice-widget-button-fg, #FFFFFF)',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            textDecoration: 'none',
            marginTop: 6,
            flexShrink: 0,
          }}
        >
          View product
        </a>
      )}
    </div>
  );
}

// ─── Table mode (compare_products) ────────────────────────────────────────────

function renderAttributeCell(attr: ComparisonAttribute, product: ComparisonProduct): React.ReactNode {
  switch (attr.type) {
    case 'price': {
      const v = product.price;
      if (v == null || v === '') return '—';
      const num = typeof v === 'number' ? v : Number(v.replace(/[^\d.-]/g, ''));
      if (!Number.isFinite(num) || num <= 0) return typeof v === 'string' ? v : '—';
      return (
        <span style={{ fontWeight: 700, color: 'var(--voice-widget-price, #111827)' }}>${num.toFixed(2)}</span>
      );
    }
    case 'rating': {
      // Amazon-only rating policy (2026-05-19): brand DTC reviews are
      // unreliable; only render when amazonRating is present.
      if (typeof product.amazonRating !== 'number' || product.amazonRating <= 0) return '—';
      return (
        <AmazonStarRating
          amazonRating={product.amazonRating}
          amazonReviewCount={product.amazonReviewCount}
        />
      );
    }
    case 'availability':
      if (product.isAvailable == null) return '—';
      return product.isAvailable ? (
        <span style={{ color: 'var(--voice-widget-badge-fg, #059669)', fontWeight: 600, fontSize: 12 }}>In stock</span>
      ) : (
        <span style={{ color: 'var(--voice-widget-warn, #DC2626)', fontWeight: 600, fontSize: 12 }}>
          Out of stock
        </span>
      );
    case 'reward':
      return product.hasRewards && product.totalRewardAmount != null && product.totalRewardAmount > 0 ? (
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--voice-widget-badge-fg, #059669)',
            background: 'var(--voice-widget-badge-bg, #ECFDF5)',
            padding: '2px 8px',
            borderRadius: 999,
          }}
        >
          ${product.totalRewardAmount.toFixed(2)} back
        </span>
      ) : (
        <span style={{ color: 'var(--voice-widget-muted, #6B7280)' }}>None</span>
      );
    case 'bullets': {
      const bullets = (product as Record<string, unknown>)[attr.key];
      if (!Array.isArray(bullets) || bullets.length === 0) return '—';
      return (
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, lineHeight: 1.5, color: 'var(--voice-widget-fg, #111827)' }}>
          {(bullets as string[]).slice(0, 3).map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      );
    }
    default: {
      const raw = (product as Record<string, unknown>)[attr.key];
      if (raw == null || raw === '' || raw === '—') return '—';
      return String(raw);
    }
  }
}

const ComparisonTable: React.FC<{
  products: ComparisonProduct[];
  attributes: ComparisonAttribute[];
  isCompact: boolean;
}> = ({ products, attributes, isCompact }) => {
  // decisions/078 follow-up — on mobile, fixed-percentage columns squash
  // the comparison table (3 products + label column on a 360px viewport
  // leaves ~63px per product column, which wraps every title and button
  // label awkwardly). Wrap the table in a horizontally-scrollable region
  // with a generous per-column minWidth so the layout stays readable; the
  // user swipes left/right to see all columns. Desktop keeps the
  // fixed-layout fit-to-container behavior.
  const labelColWidth = isCompact ? 100 : 110;
  const productColMinWidth = isCompact ? 150 : 0;
  const tableMinWidth = isCompact ? labelColWidth + productColMinWidth * products.length : undefined;

  return (
    <div
      style={{
        width: '100%',
        boxSizing: 'border-box',
        padding: '8px 8px 12px',
        overflowX: isCompact ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <table
        style={{
          width: '100%',
          minWidth: tableMinWidth,
          borderCollapse: 'separate',
          borderSpacing: 0,
          background: 'var(--voice-widget-card-bg, #FFFFFF)',
          borderRadius: 14,
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          tableLayout: 'fixed',
          fontSize: 12,
          color: 'var(--voice-widget-fg, #111827)',
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                width: labelColWidth,
                background: 'var(--voice-widget-table-head-bg, #F9FAFB)',
                borderBottom: '1px solid var(--voice-widget-border, #E5E7EB)',
              }}
            />
            {products.map((p) => {
              const href = p.retailerProductPageUrl;
              const linkProps = href ? { href, target: '_blank' as const, rel: 'noreferrer' as const } : null;
              return (
                <th
                  key={p.stacklineSku}
                  style={{
                    padding: '14px 12px 10px',
                    textAlign: 'center',
                    verticalAlign: 'top',
                    fontWeight: 600,
                    borderBottom: '1px solid var(--voice-widget-border, #E5E7EB)',
                    background: 'var(--voice-widget-table-head-bg, #F9FAFB)',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    {p.imageUrl && (
                      linkProps ? (
                        <a {...linkProps} style={{ lineHeight: 0 }}>
                          <img
                            src={p.imageUrl}
                            alt={p.title}
                            style={{
                              width: 64,
                              height: 64,
                              objectFit: 'contain',
                              borderRadius: 6,
                              background: 'var(--voice-widget-img-bg, #F9FAFB)',
                            }}
                          />
                        </a>
                      ) : (
                        <img
                          src={p.imageUrl}
                          alt={p.title}
                          style={{
                            width: 64,
                            height: 64,
                            objectFit: 'contain',
                            borderRadius: 6,
                            background: 'var(--voice-widget-img-bg, #F9FAFB)',
                          }}
                        />
                      )
                    )}
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--voice-widget-fg, #111827)',
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {p.title}
                    </div>
                    {linkProps && (
                      <a
                        {...linkProps}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'var(--voice-widget-button-bg, #111827)',
                          color: 'var(--voice-widget-button-fg, #FFFFFF)',
                          borderRadius: 999,
                          padding: '6px 14px',
                          fontSize: 11,
                          fontWeight: 600,
                          textDecoration: 'none',
                        }}
                      >
                        View product
                      </a>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {attributes.map((attr, rowIdx) => (
            <tr
              key={attr.key}
              style={{
                background:
                  rowIdx % 2 === 0
                    ? 'var(--voice-widget-card-bg, #FFFFFF)'
                    : 'var(--voice-widget-table-row-alt-bg, #F9FAFB)',
              }}
            >
              <td
                style={{
                  padding: '10px 12px',
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.3,
                  color: 'var(--voice-widget-muted, #6B7280)',
                  borderBottom: '1px solid var(--voice-widget-border, #E5E7EB)',
                  verticalAlign: 'top',
                }}
              >
                {attr.label}
              </td>
              {products.map((p) => (
                <td
                  key={p.stacklineSku}
                  style={{
                    padding: '10px 12px',
                    textAlign: 'center',
                    verticalAlign: 'top',
                    borderBottom: '1px solid var(--voice-widget-border, #E5E7EB)',
                    color: 'var(--voice-widget-fg, #111827)',
                    fontSize: 12,
                    lineHeight: 1.4,
                  }}
                >
                  {renderAttributeCell(attr, p)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Top-level dispatcher ─────────────────────────────────────────────────────

export interface ComparisonWidgetProps {
  data: ComparisonWidgetData;
}

export const ComparisonWidget: React.FC<ComparisonWidgetProps> = ({ data }) => {
  const products = data.products.slice(0, 3);
  const hasAttributes = Array.isArray(data.attributes) && data.attributes.length > 0;
  const isCompact = useIsCompactVoiceLayout();

  // Table mode (compare_products).
  if (hasAttributes) {
    return (
      <div
        style={{
          width: '100%',
          maxWidth: 768,
          padding: '12px 8px 8px',
          boxSizing: 'border-box',
          margin: '0 auto',
        }}
      >
        <style>{WIDGET_KEYFRAMES}</style>
        <ComparisonTable
          products={products}
          attributes={data.attributes as ComparisonAttribute[]}
          isCompact={isCompact}
        />
      </div>
    );
  }

  // Card-grid mode (search_products / get_product_details).
  // Each `ProductCard` is capped internally at maxWidth: 230px. We use a
  // `repeat(N, minmax(0, 230px))` track instead of `1fr` so a single
  // card doesn't stretch full-width when only one product is returned,
  // and a row of 2-3 cards stays at the narrow 230px ceiling.
  // `justifyContent: center` then centers the row inside the 768px
  // container.
  //
  // Mobile (compact): single column, still capped at 230px and centered.
  const gridTemplateColumns = isCompact
    ? 'minmax(0, 230px)'
    : `repeat(${products.length}, minmax(0, 230px))`;

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 768,
        padding: '16px 12px 8px',
        boxSizing: 'border-box',
        margin: '0 auto',
      }}
    >
      <style>{WIDGET_KEYFRAMES}</style>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns,
          justifyContent: 'center',
          gap: 10,
          alignItems: 'stretch',
        }}
      >
        {products.map((p) => (
          <ProductCard key={p.stacklineSku} product={p} />
        ))}
      </div>
    </div>
  );
};

export default ComparisonWidget;
